import React, { useEffect, useState } from "react";
import { getPassport, savePassport } from "../../api/passport";
import { getLang } from "../../store/uiStore";
import FieldInput from "../../components/FieldInput";
import { getRegions, getDistricts } from "../../api/region";
import { getDictionaries, getDictionaryItems } from "../../api/dictionary";
import { getUser } from "../../store/authStore";
import PassportComments from "../../components/PassportComments";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSelectedClinic,
  setSelectedClinic,
} from "../../store/selectedClinicStore";

import "./Passport.css";

export default function Passport() {
  const user = getUser();

  const isInspector = [
    "regional_inspector",
    "clinic_inspector",
    "central",
  ].includes(user?.role);

  const navigate = useNavigate();
  const { clinicId } = useParams();
  const clinic = getSelectedClinic();

  useEffect(() => {
    if (clinicId && user?.role !== "clinic") {
      setSelectedClinic({ id: Number(clinicId) });
    }
  }, [clinicId, user]);

  useEffect(() => {
    const inspectorRoles = [
      "regional_inspector",
      "clinic_inspector",
      "central",
    ];

    if (
      inspectorRoles.includes(user?.role) &&
      !clinic &&
      !clinicId
    ) {
      navigate("/clinics");
    }
  }, [clinic, clinicId, navigate, user]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [regions, setRegions] = useState([]);
  const [districtsMap, setDistrictsMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [conditions, setConditions] = useState([]);

  const lang = getLang();

  const t = {
    title: lang === "ru" ? "Паспорт учреждения" : "Шиносномаи муассиса",
    edit: lang === "ru" ? "Редактировать" : "Таҳрир",
    cancel: lang === "ru" ? "Отмена" : "Бекор кардан",
    save: lang === "ru" ? "Сохранить" : "Сабт кардан",
    saving: lang === "ru" ? "Сохранение..." : "Сабт шуда истодааст...",
    loading: lang === "ru" ? "Загрузка..." : "Боргирӣ...",
    noData: lang === "ru" ? "Нет данных" : "Маълумот нест",
    equipment: lang === "ru" ? "Оборудование" : "Таҷҳизот",
    branches: lang === "ru" ? "Филиалы" : "Филиалҳо",
    add: lang === "ru" ? "Добавить" : "Илова кардан",
    name: lang === "ru" ? "Название" : "Ном",
    brand: lang === "ru" ? "Бренд" : "Бренд",
    year: lang === "ru" ? "Год" : "Сол",
    country: lang === "ru" ? "Страна" : "Кишвар",
    condition: lang === "ru" ? "Состояние" : "Ҳолат",
    manager: lang === "ru" ? "Руководитель" : "Роҳбар",
    phone: lang === "ru" ? "Телефон" : "Телефон",
    region: lang === "ru" ? "Область" : "Вилоят",
    district: lang === "ru" ? "Район" : "Ноҳия",
    address: lang === "ru" ? "Адрес" : "Суроға",
    inn: lang === "ru" ? "ИНН" : "РМА",
    empty: lang === "ru" ? "Нет данных" : "Маълумот нест",
    alertSaved: lang === "ru" ? "Сохранено" : "Сабт шуд",
    alertErrors: lang === "ru" ? "Исправьте ошибки:\n\n" : "Хатогиҳоро ислоҳ кунед:\n\n",
    fill: lang === "ru" ? "заполните поле" : "майдонро пур кунед",
  };

  const loadDistricts = (regionId) => {
    if (!regionId) return;

    const id = typeof regionId === "object" ? regionId.id : regionId;

    getDistricts(id).then((res) => {
      setDistrictsMap((prev) => ({
        ...prev,
        [String(id)]: res.data,
      }));
    });
  };

  useEffect(() => {
    if (user?.role === "clinic") {
      load();
      return;
    }

    if (!clinic?.id) return;

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id, clinicId, user?.role]);

  useEffect(() => {
    getRegions().then((res) => setRegions(res.data));

    getDictionaries().then((res) => {
      const dict = res.data.find((d) => d.code === "equipment_condition");

      if (dict) {
        getDictionaryItems(dict.id).then((itemsRes) => {
          setConditions(itemsRes.data);
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!data?.sections) return;

    setFormData((prev) => {
      let changed = false;
      const newData = { ...prev };

      data.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (!field.parent_field || field.condition_value === null) return;

          const parentVal = prev[field.parent_field];
          let shouldBeEmpty = true;

          if (Array.isArray(parentVal)) {
            shouldBeEmpty = !parentVal.some(
              (v) => String(v) === String(field.condition_value)
            );
          } else {
            shouldBeEmpty =
              String(parentVal) !== String(field.condition_value);
          }

          if (shouldBeEmpty && newData[field.id]?.length > 0) {
            newData[field.id] = [];
            changed = true;
          }
        });
      });

      if (!changed) return prev;
      return newData;
    });
  }, [data?.sections, formData]);

  function getRegionId(region) {
    return typeof region === "object" && region !== null ? region.id : region;
  }

  function load() {
    setLoading(true);

    getPassport()
      .then((res) => {
        res.data.sections.forEach((section) => {
          section.fields.forEach((f) => {
            if (f.id === 21) {
              f.dictionary_id = 6;
            }
          });
        });

        setData(res.data);

        const initial = {};

        res.data.sections.forEach((section) => {
          section.fields.forEach((f) => {
            if (f.field_type === "array") {
              if (Array.isArray(f.multi_values) && f.multi_values.length > 0) {
                initial[f.id] = f.multi_values.reduce((acc, v) => {
                  if (Array.isArray(v.value)) return [...acc, ...v.value];
                  if (
                    v.value !== undefined &&
                    v.value !== null &&
                    v.value !== ""
                  ) {
                    return [...acc, v.value];
                  }
                  return acc;
                }, []);
              } else if (f.values?.length > 0) {
                initial[f.id] = [f.values[0].value];
              } else {
                initial[f.id] = [];
              }
            } else if (f.field_type === "multiselect") {
              initial[f.id] = Array.isArray(f.values) ? f.values : [];
            } else if (f.field_type === "working_hours") {
              if (f.values?.length > 0) {
                const val = f.values[0].value;
                initial[f.id] =
                  typeof val === "object" && val !== null
                    ? val
                    : { days: [], from: "", to: "" };
              } else {
                initial[f.id] = { days: [], from: "", to: "" };
              }
            } else {
              let rawValue = null;

              if (f.values && f.values.length > 0) {
                const val = f.values[0];
                rawValue =
                  val && typeof val === "object" && "value" in val
                    ? val.value
                    : val;
              }

              if (rawValue !== null && rawValue !== undefined) {
                initial[f.id] =
                  typeof rawValue === "object" ? rawValue.id ?? "" : rawValue;
              } else {
                initial[f.id] = "";
              }
            }
          });
        });

        setFormData(initial);
        setInitialFormData(initial);
      })
      .catch((err) => {
        console.error("Passport error:", err);
        setData(null);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!data?.branches) return;

    data.branches.forEach((b) => {
      const regionId =
        typeof b.region === "object" ? b.region.id : b.region;

      if (regionId) loadDistricts(regionId);
    });
  }, [data]);

  function validateForm() {
    const errors = [];

    data.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.field_type === "working_hours") {
          const v = formData[field.id];
          if (!v) return;

          const { days, from, to } = v;
          if (!days?.length && !from && !to) return;

          const isValidTime = (t) =>
            /^([01]\d|2[0-3]):([0-5]\d)$/.test(t);

          if (!isValidTime(from) || !isValidTime(to)) {
            errors.push(
              lang === "ru"
                ? `${field.name_ru}: неверный формат времени (HH:MM)`
                : `${field.name_tj}: формати вақт нодуруст аст (HH:MM)`
            );
            return;
          }

          if (!days?.length) {
            errors.push(
              lang === "ru"
                ? `${field.name_ru}: выберите дни и укажите время (HH:MM)`
                : `${field.name_tj}: рӯзҳо ва вақтро интихоб кунед (HH:MM)`
            );
            return;
          }

          if (from >= to) {
            errors.push(
              lang === "ru"
                ? `${field.name_ru}: время начала должно быть меньше окончания`
                : `${field.name_tj}: вақти оғоз бояд аз анҷом хурд бошад`
            );
          }
        }

        if (field.is_required) {
          const value = formData[field.id];

          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
          ) {
            errors.push(
              lang === "ru"
                ? `${field.name_ru}: ${t.fill}`
                : `${field.name_tj}: ${t.fill}`
            );
          }
        }
      });
    });

    return errors;
  }

  function buildPayload() {
    const passport_values = {};

    Object.keys(formData).forEach((fieldId) => {
      let current = formData[fieldId];
      let initial = initialFormData[fieldId];

      const field = data.sections
        .flatMap((s) => s.fields)
        .find((f) => String(f.id) === String(fieldId));

      if (field?.field_type === "working_hours") {
        passport_values[fieldId] = current;
        return;
      }

      if (Array.isArray(current)) {
        current = current.flatMap((v) => (Array.isArray(v) ? v : [v]));
      }

      if (Array.isArray(initial)) {
        initial = initial.flatMap((v) => (Array.isArray(v) ? v : [v]));
      }

      if (Array.isArray(current) && Array.isArray(initial)) {
        const isSame =
          current.length === initial.length &&
          current.every((v, i) => String(v) === String(initial[i]));

        if (isSame) return;
      }

      passport_values[fieldId] = current;
    });

    return {
      passport_values,
      equipment: (data?.equipment || []).map((item) => ({
        ...item,
        condition:
          typeof item.condition === "object" && item.condition !== null
            ? item.condition.id
            : item.condition || null,
        production_year: item.production_year
          ? Number(item.production_year)
          : null,
      })),
      branches: (data?.branches || []).map((b) => ({
        ...b,
        region: typeof b.region === "object" ? b.region.id : b.region,
        district: typeof b.district === "object" ? b.district.id : b.district,
      })),
    };
  }

  function handleSave() {
    const errors = validateForm();

    if (errors.length > 0) {
      alert(t.alertErrors + errors.join("\n"));
      return;
    }

    setSaving(true);

    savePassport(buildPayload())
      .then(() => {
        alert(t.alertSaved);
        setIsEditing(false);
        load();
      })
      .catch((err) => {
        alert(JSON.stringify(err.response?.data));
      })
      .finally(() => setSaving(false));
  }

  function getRegionName(region) {
    if (typeof region === "object" && region !== null) {
      return lang === "ru" ? region.name_ru : region.name_tj;
    }

    const found = regions.find((r) => String(r.id) === String(region));

    return found
      ? lang === "ru"
        ? found.name_ru
        : found.name_tj
      : region;
  }

  function getDistrictName(region, district) {
    if (typeof district === "object" && district !== null) {
      return lang === "ru" ? district.name_ru : district.name_tj;
    }

    const key =
      typeof region === "object" ? String(region.id) : String(region);

    const districts = districtsMap[key];

    if (!districts) return "...";

    const found = districts.find((d) => String(d.id) === String(district));

    return found
      ? lang === "ru"
        ? found.name_ru
        : found.name_tj
      : district;
  }

  function updateBranch(index, field, value) {
    const newBranches = [...(data.branches || [])];

    if (field === "region") {
      newBranches[index].region = value;
      newBranches[index].district = null;
      loadDistricts(value);
    } else {
      newBranches[index][field] = value;
    }

    setData({ ...data, branches: newBranches });
  }

  function updateEq(index, field, value) {
    const newEquipment = [...(data.equipment || [])];

    if (field === "condition") {
      value = value ? Number(value) : null;
    }

    newEquipment[index][field] = value;
    setData({ ...data, equipment: newEquipment });
  }

  if (loading) {
    return (
      <main className="nizom-passport-page">
        <div className="nizom-passport-container">
          <div className="nizom-passport-state">{t.loading}</div>
        </div>
      </main>
    );
  }

  if (!data || !data.sections) {
    return (
      <main className="nizom-passport-page">
        <div className="nizom-passport-container">
          <div className="nizom-passport-state">{t.noData}</div>
        </div>
      </main>
    );
  }

  return (
    <main className="nizom-passport-page">
      <div className="nizom-passport-container">
        <header className="nizom-passport-header">
          <div>
            <div className="nizom-passport-eyebrow">
              {lang === "ru" ? "Данные учреждения" : "Маълумоти муассиса"}
            </div>
            <h1>{t.title}</h1>
          </div>

          {!isInspector && (
            <div className="nizom-passport-header-actions">
              <button
                type="button"
                className={`passport-btn ${
                  isEditing ? "secondary" : "primary"
                }`}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? t.cancel : t.edit}
              </button>
            </div>
          )}
        </header>

        <div className="nizom-passport-sections">
          {data.sections.map((section) => (
            <section key={section.id} className="passport-section">
              <div className="passport-section-title">
                {lang === "ru" ? section.name_ru : section.name_tj}
              </div>

              <div className="passport-fields">
                {section.fields.map((field) => {
                  let isDisabledCustom = false;
                  let dynamicDictionaryId = field.dictionary;

                  if (field.parent_field) {
                    const parentRaw = formData[field.parent_field];

                    const isEmpty =
                      parentRaw === undefined ||
                      parentRaw === null ||
                      parentRaw === "" ||
                      (Array.isArray(parentRaw) && parentRaw.length === 0);

                    if (isEmpty) isDisabledCustom = true;

                    if (
                      field.condition_value !== null &&
                      field.condition_value !== undefined
                    ) {
                      let isMatch = false;

                      if (Array.isArray(parentRaw)) {
                        isMatch = parentRaw.some(
                          (v) => String(v) === String(field.condition_value)
                        );
                      } else {
                        isMatch =
                          parentRaw != null &&
                          String(parentRaw) === String(field.condition_value);
                      }

                      if (isMatch) {
                        isDisabledCustom = false;

                        if (
                          field.condition_dictionary &&
                          dynamicDictionaryId !== field.condition_dictionary
                        ) {
                          dynamicDictionaryId = field.condition_dictionary;
                        }
                      } else {
                        isDisabledCustom = true;
                      }
                    }
                  }

                  return (
                    <div key={field.id} className="passport-field-row">
                      <div className="passport-field-label">
                        <span>
                          {lang === "ru" ? field.name_ru : field.name_tj}
                        </span>
                        {field.is_required && (
                          <span className="passport-required" aria-hidden="true">
                            *
                          </span>
                        )}
                      </div>

                      <div className="passport-field-value">
                        {isEditing ? (
                          <FieldInput
                            field={field}
                            isDisabledCustom={isDisabledCustom}
                            dictionary_id={dynamicDictionaryId}
                            value={
                              Array.isArray(formData[field.id]) &&
                              formData[field.id].length === 1 &&
                              Array.isArray(formData[field.id][0])
                                ? formData[field.id][0]
                                : formData[field.id]
                            }
                            formData={formData}
                            lang={lang}
                            isEditing={isEditing}
                            onChange={(val) =>
                              setFormData((prev) => {
                                let newVal = val;

                                if (
                                  Array.isArray(newVal) &&
                                  newVal.length === 1 &&
                                  Array.isArray(newVal[0])
                                ) {
                                  newVal = newVal[0];
                                }

                                return {
                                  ...prev,
                                  [field.id]: Array.isArray(newVal)
                                    ? [...newVal]
                                    : newVal,
                                };
                              })
                            }
                          />
                        ) : (
                          renderValue(field, lang, regions, districtsMap, data)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <PassportCollectionSection
          title={t.equipment}
          addLabel={t.add}
          editing={isEditing}
          data={data.equipment || []}
          type="equipment"
          lang={lang}
          conditions={conditions}
          onAdd={() =>
            setData({
              ...data,
              equipment: [...(data.equipment || []), {}],
            })
          }
          onUpdate={updateEq}
          t={t}
        />

        <PassportCollectionSection
          title={t.branches}
          addLabel={t.add}
          editing={isEditing}
          data={data.branches || []}
          type="branches"
          lang={lang}
          regions={regions}
          districtsMap={districtsMap}
          onAdd={() =>
            setData({
              ...data,
              branches: [...(data.branches || []), {}],
            })
          }
          onUpdate={updateBranch}
          getRegionId={getRegionId}
          getRegionName={getRegionName}
          getDistrictName={getDistrictName}
          t={t}
        />

        {isEditing && (
          <div className="passport-save-bar">
            <button
              type="button"
              className="passport-btn secondary"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              {t.cancel}
            </button>

            <button
              type="button"
              className="passport-btn primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t.saving : t.save}
            </button>
          </div>
        )}

        {isInspector && <PassportComments />}
      </div>
    </main>
  );
}

function PassportCollectionSection({
  title,
  addLabel,
  editing,
  data,
  type,
  lang,
  conditions,
  regions,
  districtsMap,
  onAdd,
  onUpdate,
  getRegionId,
  getRegionName,
  getDistrictName,
  t,
}) {
  return (
    <section className="passport-section passport-collection-section">
      <div className="passport-section-heading">
        <div className="passport-section-title">{title}</div>

        {editing && (
          <button type="button" className="passport-add-btn" onClick={onAdd}>
            <span aria-hidden="true">+</span>
            {addLabel}
          </button>
        )}
      </div>

      {type === "equipment" ? (
        <>
          <div className="passport-collection-table-wrap">
            <table className="passport-collection-table">
              <thead>
                <tr>
                  <th>{t.name}</th>
                  <th>{t.brand}</th>
                  <th>{t.year}</th>
                  <th>{t.country}</th>
                  <th>{t.condition}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, i) => (
                  <tr key={i}>
                    <td>
                      {editing ? (
                        <input
                          value={item.name || ""}
                          onChange={(e) => onUpdate(i, "name", e.target.value)}
                          disabled={false}
                        />
                      ) : (
                        item.name || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={item.brand || ""}
                          onChange={(e) => onUpdate(i, "brand", e.target.value)}
                        />
                      ) : (
                        item.brand || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          value={item.production_year || ""}
                          onChange={(e) =>
                            onUpdate(i, "production_year", e.target.value)
                          }
                        />
                      ) : (
                        item.production_year || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={item.country || ""}
                          onChange={(e) =>
                            onUpdate(i, "country", e.target.value)
                          }
                        />
                      ) : (
                        item.country || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <select
                          value={
                            typeof item.condition === "object" &&
                            item.condition !== null
                              ? item.condition.id
                              : item.condition || ""
                          }
                          onChange={(e) =>
                            onUpdate(i, "condition", e.target.value)
                          }
                        >
                          <option value="">{t.condition}</option>
                          {conditions.map((c) => (
                            <option key={c.id} value={c.id}>
                              {lang === "ru" ? c.name_ru : c.name_tj}
                            </option>
                          ))}
                        </select>
                      ) : (
                        conditions.find(
                          (c) =>
                            c.id ===
                            (typeof item.condition === "object"
                              ? item.condition.id
                              : item.condition)
                        )?.[lang === "ru" ? "name_ru" : "name_tj"] || "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="passport-mobile-cards">
            {data.map((item, i) => (
              <article className="passport-mobile-card" key={i}>
                <div className="passport-mobile-card-title">
                  {item.name || t.name}
                </div>
                <CollectionMobileField label={t.brand} value={item.brand} />
                <CollectionMobileField
                  label={t.year}
                  value={item.production_year}
                />
                <CollectionMobileField label={t.country} value={item.country} />
                <CollectionMobileField
                  label={t.condition}
                  value={
                    conditions.find(
                      (c) =>
                        c.id ===
                        (typeof item.condition === "object"
                          ? item.condition.id
                          : item.condition)
                    )?.[lang === "ru" ? "name_ru" : "name_tj"]
                  }
                />
              </article>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="passport-collection-table-wrap">
            <table className="passport-collection-table">
              <thead>
                <tr>
                  <th>{t.manager}</th>
                  <th>{t.phone}</th>
                  <th>{t.region}</th>
                  <th>{t.district}</th>
                  <th>{t.address}</th>
                  <th>{t.inn}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((branch, i) => (
                  <tr key={i}>
                    <td>
                      {editing ? (
                        <input
                          value={branch.manager_name || ""}
                          onChange={(e) =>
                            onUpdate(i, "manager_name", e.target.value)
                          }
                        />
                      ) : (
                        branch.manager_name || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={branch.manager_phone || ""}
                          onChange={(e) =>
                            onUpdate(i, "manager_phone", e.target.value)
                          }
                        />
                      ) : (
                        branch.manager_phone || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <select
                          value={
                            typeof branch.region === "object" &&
                            branch.region !== null
                              ? branch.region.id
                              : branch.region || ""
                          }
                          onChange={(e) =>
                            onUpdate(i, "region", e.target.value)
                          }
                        >
                          <option value="">{t.region}</option>
                          {regions.map((r) => (
                            <option key={r.id} value={r.id}>
                              {lang === "ru" ? r.name_ru : r.name_tj}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getRegionName(branch.region) || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <select
                          value={
                            typeof branch.district === "object" &&
                            branch.district !== null
                              ? branch.district.id
                              : branch.district || ""
                          }
                          onChange={(e) =>
                            onUpdate(i, "district", e.target.value)
                          }
                          disabled={!getRegionId(branch.region)}
                        >
                          <option value="">{t.district}</option>
                          {(
                            districtsMap[String(getRegionId(branch.region))] ||
                            []
                          ).map((d) => (
                            <option key={d.id} value={d.id}>
                              {lang === "ru" ? d.name_ru : d.name_tj}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getDistrictName(branch.region, branch.district) || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={branch.address || ""}
                          onChange={(e) =>
                            onUpdate(i, "address", e.target.value)
                          }
                        />
                      ) : (
                        branch.address || "-"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={branch.inn || ""}
                          onChange={(e) => onUpdate(i, "inn", e.target.value)}
                        />
                      ) : (
                        branch.inn || "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="passport-mobile-cards">
            {data.map((branch, i) => (
              <article className="passport-mobile-card" key={i}>
                <div className="passport-mobile-card-title">
                  {branch.manager_name || t.manager}
                </div>
                <CollectionMobileField label={t.phone} value={branch.manager_phone} />
                <CollectionMobileField
                  label={t.region}
                  value={getRegionName(branch.region)}
                />
                <CollectionMobileField
                  label={t.district}
                  value={getDistrictName(branch.region, branch.district)}
                />
                <CollectionMobileField label={t.address} value={branch.address} />
                <CollectionMobileField label={t.inn} value={branch.inn} />
              </article>
            ))}
          </div>
        </>
      )}

      {data.length === 0 && (
        <div className="passport-collection-empty">{t.empty}</div>
      )}
    </section>
  );
}

function CollectionMobileField({ label, value }) {
  return (
    <div className="passport-mobile-field">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function renderValue(field, lang, regions, districtsMap) {
  const single = field.values?.[0]?.value;
  const multi = field.multi_values?.map((v) =>
    lang === "ru" ? v.name_ru : v.name_tj
  );

  if (field.field_type === "working_hours") {
    const v = field.values?.[0]?.value;

    if (!v || !v.days?.length) return "-";

    const dayLabels = {
      mon: lang === "ru" ? "Пн" : "Дш",
      tue: lang === "ru" ? "Вт" : "Сш",
      wed: lang === "ru" ? "Ср" : "Чш",
      thu: lang === "ru" ? "Чт" : "Пш",
      fri: lang === "ru" ? "Пт" : "Ҷм",
      sat: lang === "ru" ? "Сб" : "Шб",
      sun: lang === "ru" ? "Вс" : "Як",
    };

    const days = v.days.map((d) => dayLabels[d]).join(", ");
    return `${days}: ${v.from} — ${v.to}`;
  }

  let value = single !== undefined ? single : multi;

  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return lang === "ru" ? value.name_ru : value.name_tj;
  }

  if (field.dictionary_items && field.dictionary_items.length > 0) {
    const findLabel = (val) => {
      const found = field.dictionary_items.find(
        (item) => String(item.id) === String(val)
      );

      return found
        ? lang === "ru"
          ? found.name_ru
          : found.name_tj
        : val;
    };

    if (Array.isArray(value)) return value.map(findLabel).join(", ");
    return findLabel(value);
  }

  if (field.field_type === "region") {
    if (!regions?.length) return "...";

    const region = regions.find((r) => String(r.id) === String(value));

    return region
      ? lang === "ru"
        ? region.name_ru
        : region.name_tj
      : value;
  }

  if (field.field_type === "district") {
    const allDistricts = Object.values(districtsMap || {}).flat();

    if (!allDistricts.length) return "...";

    const district = allDistricts.find(
      (d) => String(d.id) === String(value)
    );

    return district
      ? lang === "ru"
        ? district.name_ru
        : district.name_tj
      : value;
  }

  if (!value || (Array.isArray(value) && value.length === 0)) return "-";

  if (field.field_type === "boolean") {
    return value
      ? lang === "ru"
        ? "Да"
        : "Ҳа"
      : lang === "ru"
      ? "Нет"
      : "Не";
  }

  if (Array.isArray(value)) return value.join(", ");
  return value;
}
